import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

// §55 one-click list cleanup: hard bounces + no opens after 5 emails over 90+ days. Removal is permanent.
const LC = "/admin/mk/list-cleanup";
const csv = (rows) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([["email,name,reason,detail", ...rows.map((r) => [r.email, r.name || "", r.reason, r.detail].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))].join("\n")], { type: "text/csv" }));
  a.download = `rpopro-list-cleanup-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
};

const Group = ({ title, rows, testid }) => (
  <div data-testid={testid}>
    <p className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">{title} ({rows.length})</p>
    <div className="max-h-40 overflow-y-auto rounded-lg border border-[#27272A]">{rows.slice(0, 200).map((r) => <p key={r.email} className="flex justify-between gap-2 border-b border-[#1f1f23] px-2 py-1 text-[11px]"><span className="min-w-0 truncate font-mono text-zinc-200">{r.email}</span><span className="shrink-0 text-zinc-500">{r.detail}</span></p>)}
      {!rows.length && <p className="px-2 py-2 text-[11px] text-zinc-600">None</p>}</div>
  </div>
);

export const ListCleanup = () => {
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState("");
  const [typed, setTyped] = useState("");
  const [done, setDone] = useState(null);
  const preview = async () => {
    setBusy("pv"); setTyped("");
    try { const { data } = await api.post(`${LC}/preview`, {}); setPv(data); }
    catch (e) { toast.error(e.response?.status === 404 ? "List cleanup isn't live on the backend yet (backend-spec §55)." : errMsg(e)); } finally { setBusy(""); }
  };
  const bounced = (pv?.bounced || []).map((r) => ({ ...r, reason: "bounced", detail: `bounced${r.bounced_at ? ` ${new Date(r.bounced_at).toLocaleDateString()}` : ""}` }));
  const cold = (pv?.never_opened || []).map((r) => ({ ...r, reason: "never_opened", detail: `${r.sent ?? 5}+ emails, 0 opens` }));
  const all = [...bounced, ...cold];
  const run = async () => {
    setBusy("run");
    try { const { data } = await api.post(`${LC}/run`, { preview_id: pv.id, confirm: "REMOVE" }); setDone(data); setPv(null); toast.success(data.message || `Removed ${data.removed} addresses`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-[#27272A] bg-[#0f0f12] p-4" data-testid="list-cleanup">
      <Sparkles className="h-5 w-5 text-[#D4AF37]" />
      <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-zinc-100">Clean my list</p>
        <p className="text-[11px] text-zinc-500">Before a big send, remove addresses that hard-bounced and people who got 5+ emails over 90+ days without opening one.{done ? ` Last clean: ${done.removed} removed.` : ""}</p></div>
      <Button onClick={preview} disabled={!!busy} className="bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="list-cleanup-btn">{busy === "pv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}Clean my list</Button>
      <Dialog open={!!pv} onOpenChange={(o) => !o && setPv(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-[#27272A] bg-[#0f0f12]" data-testid="list-cleanup-dialog">
          <DialogTitle className="font-display text-zinc-50">Clean your list</DialogTitle>
          {pv && (!all.length ? <p className="text-sm text-zinc-300" data-testid="list-cleanup-clean">Your list is already clean. Nothing to remove out of {pv.total_audience ?? 0} addresses.</p> : <>
            <p className="text-sm text-zinc-300" data-testid="list-cleanup-summary">{all.length} of {pv.total_audience ?? "?"} addresses will be removed: {bounced.length} bounced · {cold.length} never opened.</p>
            <Group title="Bounced" rows={bounced} testid="list-cleanup-bounced" />
            <Group title="Never opened" rows={cold} testid="list-cleanup-cold" />
            <Button variant="outline" onClick={() => csv(all)} className="border-[#27272A]" data-testid="list-cleanup-download"><Download className="h-4 w-4" />Download this list first (CSV)</Button>
            <p className="rounded-lg border border-red-500/40 bg-red-500/5 p-2 text-xs text-red-200">This is permanent. These addresses are deleted from your marketing list and can't be restored. Type REMOVE to confirm.</p>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="REMOVE" className="h-9 text-sm" data-testid="list-cleanup-confirm-input" />
            <Button onClick={run} disabled={typed.trim() !== "REMOVE" || !!busy} className="bg-red-500 text-white hover:bg-red-400" data-testid="list-cleanup-run">{busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}Remove {all.length} addresses</Button>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
};
